import type {
  ActivityCategory,
  AdminActivityListResponse,
  AdminMeResponse,
  AdminMemberPortfolio,
  AdminReferralRow,
  AdminMetricsResponse,
  AdminMoneyTotals,
  AdminOutcomesResponse,
  AdminReferralsResponse,
  AdminUserKyc,
  AdminNotificationPreview,
  AdminSendNotificationBody,
  BookScope,
  ChainsResponse,
  AdminSendNotificationResponse,
  GrantStaffBody,
  GrantStaffResponse,
  NotificationScreensResponse,
  StaffListResponse,
  StaffLookupResponse,
  RevokedStaffMember,
  AdminDeviceBinding,
  AdminDeviceBindingBody,
  AdminDeviceRecoveriesResponse,
} from "./types";

/**
 * A non-2xx from the API.
 *
 * `status` is what the routing decisions key off (401 -> sign in again, 403 ->
 * the access-refusal screen), and `message` is the server's own `message`
 * field, which several endpoints use as user-facing copy: "Recipient not
 * found", "An admin cannot revoke their own role". Screens render it verbatim
 * rather than substituting a generic string, so the console never contradicts
 * the API about why something was refused.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Field-level detail from the API's ValidationError shape, when present. */
  readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(status: number, message: string, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

/** Thrown when the browser could not reach the API at all. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Could not reach the API");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /**
   * Resolves a fresh Privy access token per request. The console deliberately
   * does NOT cache the token: Privy's SDK owns refresh, and a cached copy is
   * how a console ends up sending an expired bearer for the rest of a session.
   */
  getToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}

interface RequestOptions {
  method?: string;
  // Booleans are stringified to "true"/"false", which is exactly the string
  // enum the API declares for its boolean query params.
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
}

function buildQuery(query: RequestOptions["query"]): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

/**
 * Reads the API's error envelope: `{ status, message, statusCode, errors? }`.
 * Falls back to the HTTP status text when the body is not that shape (a proxy
 * error page, say), so a screen always has something honest to render.
 */
async function toApiError(res: Response): Promise<ApiError> {
  let message = res.statusText || `Request failed with ${res.status}`;
  let fieldErrors: Record<string, string[]> | undefined;
  try {
    const parsed: unknown = await res.json();
    if (parsed && typeof parsed === "object") {
      const envelope = parsed as { message?: unknown; errors?: unknown };
      if (typeof envelope.message === "string" && envelope.message.length > 0) {
        message = envelope.message;
      }
      if (envelope.errors && typeof envelope.errors === "object") {
        fieldErrors = envelope.errors as Record<string, string[]>;
      }
    }
  } catch {
    // Body was not JSON; the status-derived message stands.
  }
  return new ApiError(res.status, message, fieldErrors);
}

export function createApiClient({ baseUrl, getToken, fetchImpl }: ApiClientOptions) {
  const doFetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  const root = baseUrl.replace(/\/+$/, "");

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = await getToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (options.body !== undefined) headers["Content-Type"] = "application/json";

    let res: Response;
    try {
      res = await doFetch(`${root}${path}${buildQuery(options.query)}`, {
        method: options.method ?? "GET",
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      });
    } catch (err) {
      throw new NetworkError(err);
    }

    if (!res.ok) throw await toApiError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    me: () => request<AdminMeResponse>("/v1/admin/me"),

    /**
     * The consumer bootstrap, borrowed for onboarding: /v1/auth/me creates
     * the caller's user row and captures their email from Privy. The console
     * fires it once when access is refused, so that by the time this person
     * asks an admin to add them, "add by email" has something to find. The
     * response is irrelevant here.
     */
    bootstrap: () => request<unknown>("/v1/auth/me"),

    /**
     * `scope` is admin-only and must be passed identically here and to
     * `referrals`: the funnel and the table have to describe one population.
     */
    metrics: (params: { scope?: BookScope } = {}) =>
      request<AdminMetricsResponse>("/v1/admin/metrics", { query: params }),

    referrals: (
      params: {
        search?: string;
        scope?: BookScope;
        cursor?: string;
        limit?: number;
      } = {},
    ) => request<AdminReferralsResponse>("/v1/admin/referrals", { query: params }),

    /**
     * One member's book row, for the member page header. Same shape as a
     * referrals row; an RM asking about a user outside their book gets a 404
     * identical to a user who does not exist, here and on every read below.
     */
    member: (userId: string) =>
      request<AdminReferralRow>(`/v1/admin/users/${encodeURIComponent(userId)}`),

    /**
     * The member's net worth as their own Portfolio screen composes it. Slices
     * fail independently; check `partial` before trusting the total.
     */
    memberPortfolio: (userId: string) =>
      request<AdminMemberPortfolio>(
        `/v1/admin/users/${encodeURIComponent(userId)}/portfolio`,
      ),

    /**
     * The member's activity feed, the app's own rows and cursor grammar. One
     * category at a time from the console, though the API accepts several.
     */
    memberActivity: (
      userId: string,
      params: { category?: ActivityCategory; cursor?: string; limit?: number } = {},
    ) =>
      request<AdminActivityListResponse>(
        `/v1/admin/users/${encodeURIComponent(userId)}/activity`,
        { query: params },
      ),

    /**
     * One user's KYC case: verdict, the provider's own cancel reason, case id
     * and timestamps. An RM asking about a user outside their book gets a 404
     * identical to a user who does not exist.
     */
    userKyc: (userId: string) =>
      request<AdminUserKyc>(
        `/v1/admin/users/${encodeURIComponent(userId)}/kyc`,
      ),

    notificationScreens: () =>
      request<NotificationScreensResponse>("/v1/admin/notification-screens"),

    /** The same catalog the app is served. Public; the bearer is harmless. */
    chains: () => request<ChainsResponse>("/v1/config/chains"),

    /** Renders a guide without sending it: the copy the send would store. */
    previewNotification: (body: AdminSendNotificationBody) =>
      request<AdminNotificationPreview>("/v1/admin/notifications/preview", {
        method: "POST",
        body,
      }),

    sendNotification: (body: AdminSendNotificationBody) =>
      request<AdminSendNotificationResponse>("/v1/admin/notifications", {
        method: "POST",
        body,
      }),

    /**
     * `recipientUserId` narrows to one recipient server-side. Filtering a
     * fetched page client-side instead loses every send past the first page.
     */
    outcomes: (
      params: { recipientUserId?: string; cursor?: string; limit?: number } = {},
    ) => request<AdminOutcomesResponse>("/v1/admin/notifications", { query: params }),

    staff: () => request<StaffListResponse>("/v1/admin/staff"),

    // Partial email, admin-only: who am I about to grant, while still typing.
    staffLookup: (q: string) =>
      request<StaffLookupResponse>("/v1/admin/staff/lookup", { query: { q } }),

    grantStaff: (body: GrantStaffBody) =>
      request<GrantStaffResponse>("/v1/admin/staff", { method: "POST", body }),

    // The DID contains colons, which are legal in a path segment but not worth
    // relying on: encode it so the router sees exactly one segment.
    revokeStaff: (privyDid: string) =>
      request<RevokedStaffMember>(
        `/v1/admin/staff/${encodeURIComponent(privyDid)}`,
        { method: "DELETE" },
      ),

    // Single-device login. Admin only; the API refuses all of these on your
    // own account or your own recovery.
    deviceRecoveries: () =>
      request<AdminDeviceRecoveriesResponse>("/v1/admin/device-replacements"),

    decideRecovery: (id: string, decision: "approve" | "deny", note?: string) =>
      request<unknown>(
        `/v1/admin/device-replacements/${encodeURIComponent(id)}/${decision}`,
        { method: "POST", body: note ? { note } : {} },
      ),

    deviceBinding: (userId: string) =>
      request<AdminDeviceBinding>(
        `/v1/admin/users/${encodeURIComponent(userId)}/device-binding`,
      ),

    setDeviceBinding: (userId: string, body: AdminDeviceBindingBody) =>
      request<AdminDeviceBinding>(
        `/v1/admin/users/${encodeURIComponent(userId)}/device-binding`,
        { method: "PUT", body },
      ),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * Whether the metrics response carries the admin money row.
 *
 * The contract is key ABSENCE: the API omits `money` for RMs rather than
 * sending null, so the presence test is `in`, not truthiness. `{ money: {
 * cardSpendUsd: 0, transferredToCardUsd: 0 } }` is a real admin response that
 * must render. The extra null/object check is belt-and-braces: if the API ever
 * did send `money: null`, hiding the row beats crashing on `money.cardSpendUsd`.
 */
export function hasMoney(
  metrics: AdminMetricsResponse,
): metrics is AdminMetricsResponse & { money: AdminMoneyTotals } {
  return (
    Object.prototype.hasOwnProperty.call(metrics, "money") &&
    typeof metrics.money === "object" &&
    metrics.money !== null
  );
}
